import * as React from "react"
import mediumZoom, { type Zoom } from "medium-zoom"
import { cn } from "@/lib/utils"

export interface ImageZoomProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  zoomMargin?: number
}

const ImageZoom = React.forwardRef<HTMLImageElement, ImageZoomProps>(
  ({ className, zoomMargin = 48, ...props }, ref) => {
    const zoomRef = React.useRef<Zoom | null>(null)
    const imageRef = React.useRef<HTMLImageElement | null>(null)

    React.useImperativeHandle(ref, () => imageRef.current!, [])

    React.useEffect(() => {
      let styleElement: HTMLStyleElement | null = null

      if (imageRef.current) {
        zoomRef.current = mediumZoom(imageRef.current, {
          margin: zoomMargin,
          background: "rgba(0, 0, 0, 0.9)",
          scrollOffset: 0,
        })

        // Ensure zoom overlay has high z-index to appear above other elements
        styleElement = document.createElement('style')
        styleElement.textContent = `
          .medium-zoom-overlay,
          .medium-zoom-image--opened {
            z-index: 9999 !important;
          }
        `
        document.head.appendChild(styleElement)
      }

      return () => {
        if (zoomRef.current) {
          zoomRef.current.detach()
        }
        if (styleElement) {
          document.head.removeChild(styleElement)
        }
      }
    }, [zoomMargin])

    return (
      <img
        ref={imageRef}
        className={cn("cursor-zoom-in transition-opacity hover:opacity-90", className)}
        {...props}
      />
    )
  }
)

ImageZoom.displayName = "ImageZoom"

export { ImageZoom }
